import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Submittal / Proposal format rail's internal reorder, driven by raw mouse
 * events — i.e. the way jQuery-UI's sortable drives it, and NOT the way the
 * first port did it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ WHY THIS EXISTS AT ALL.
 *
 * `makeSortable()` (`submittal.js:832-881`, `proposal.js:606-656`) is a
 * `sortable({items: '.…item-for-drag', axis: 'y'})`. Four things fall out of
 * that widget which native HTML5 DnD cannot give you, and all four were
 * reported against the React rails:
 *
 *   1. `axis: "y"` — the lifted row tracks the pointer VERTICALLY ONLY. The
 *      native drag image follows the pointer in both axes and there is no
 *      option that constrains it.
 *   2. the lifted row stays INSIDE its list. jQuery-UI positions the helper
 *      absolutely within the sortable's offset parent; the native drag image is
 *      an OS-level bitmap that floats over the whole screen (and over the other
 *      two columns of the page).
 *   3. `ui-sortable-placeholder` — an empty, item-shaped hole that opens at the
 *      position the row would land in. The port opened no hole for an internal
 *      drag: it spliced the real row into the target slot on every `dragover`,
 *      so the list never had a gap in it.
 *   4. no layout jumping. Because the helper is out of flow and the placeholder
 *      is exactly its size, the list's geometry is CONSTANT for the whole drag.
 *      The port's live splice moved the row the pointer was holding, which
 *      re-flowed every row below it and fed the new geometry straight back into
 *      the next `dragover` — the rows shuffled and overlapped under the cursor.
 *
 * So the internal drag is a pointer drag now. The LIBRARY drag (an anchor from
 * the middle column dropped into the rail) is untouched: legacy drives that one
 * with `draggable({connectToSortable})`, the anchors are already
 * `draggable="true"` in the legacy markup, and its placeholder was correct.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ THE GEOMETRY IS CACHED ONCE, AND THAT IS LOAD-BEARING.
 *
 * `refreshPositions` runs at `_mouseStart` and the intersection tests all run
 * against those boxes. Here the equivalent is `slotTops` — every item's offset
 * from the container's top, measured at drag start and never re-measured.
 *
 * It stays correct for the whole drag precisely BECAUSE of (3) and (4): the
 * placeholder is the same height as the row it replaced, so the list is the
 * same n slots of the same heights from `mousedown` to `mouseup`. Only which
 * row sits in which slot changes. The offsets are container-relative rather
 * than viewport-relative so they survive the auto-scroll below.
 */

/** jQuery-UI `$.ui.mouse`'s `distance` option. A press that never travels a
 * pixel starts no sort — which is the other half of Q22 (see the rails). */
const DRAG_DISTANCE = 1;
/** `$.ui.sortable`'s `scrollSensitivity` / `scrollSpeed` defaults. Legacy runs
 * with `scroll: true`, so a drag towards either edge of the scroll container
 * pages the rail — without it a 40-row rail can only be reordered as far as the
 * viewport reaches. Scrolling happens per `mousemove`, as jQuery-UI does it. */
const SCROLL_SENSITIVITY = 20;
const SCROLL_SPEED = 20;

export interface RailDragState {
  /** The dragged row's CURRENT index in the rail's rows — i.e. where the
   * placeholder is. */
  index: number;
  /** The helper's `top`, in px, relative to the sortable container. Already
   * clamped into the container, and the ONLY coordinate that ever moves. */
  top: number;
  /** The placeholder's height — the row's own height, so the list's total
   * height cannot change while the drag is in flight. */
  height: number;
}

interface UseRailSortableOptions {
  /** `rows.length`. Read through a ref so the listeners never go stale. */
  rowCount: number;
  /** The scroll container (`.left-column-*-scrollable`) — the auto-scroll
   * target. The sortable itself is bound to the `.row` INSIDE it. */
  scrollerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** jQuery-UI sortable's `cancel` option: a press on one of these is not a
   * sort, which is why pressing the trash never reorders anything. */
  cancelSelector: string;
  /** Shuffles the rail's rendered order — the placeholder moving. */
  onRowsMove: (from: number, to: number) => void;
  /** jQuery-UI's `stop`, fired on mouseup after a real drag. */
  onCommit: () => void;
}

export interface RailSortable {
  /** `null` when no internal drag is in flight. */
  drag: RailDragState | null;
  /** Goes on the sortable container — `#…-format-list-wrapper-row`. */
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Bind to each row's `onMouseDown`. */
  onRowMouseDown: (e: React.MouseEvent<HTMLElement>, index: number) => void;
  /**
   * jQuery-UI's `preventClickEvent`: `_mouseUp` flags the pressed element and
   * the widget's own click handler `stopImmediatePropagation`s the click that
   * trails a drag — so a reorder does NOT also select the row, and does not
   * emit Q22's second PATCH.
   */
  justDraggedRef: React.MutableRefObject<boolean>;
  /** The helper's inline style, or `undefined` for a row at rest. */
  helperStyle: (index: number) => React.CSSProperties | undefined;
}

export const useRailSortable = ({
  rowCount,
  scrollerRef,
  cancelSelector,
  onRowsMove,
  onCommit,
}: UseRailSortableOptions): RailSortable => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<RailDragState | null>(null);
  const justDraggedRef = useRef(false);
  /** Everything the document-level listeners need, refreshed every render so a
   * drag that outlives a re-render still reads the current values. */
  const latest = useRef({ rowCount, cancelSelector, onRowsMove, onCommit });
  latest.current = { rowCount, cancelSelector, onRowsMove, onCommit };
  /** Detaches whatever listeners the current press installed. */
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => () => releaseRef.current?.(), []);

  const onRowMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>, index: number) => {
      // `$.ui.mouse._mouseDown`: left button only.
      if (e.button !== 0) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest(latest.current.cancelSelector)) return;

      const row = e.currentTarget;
      const startX = e.clientX;
      const startY = e.clientY;
      let started = false;
      /** Where inside the row the pointer grabbed it. Preserved for the whole
       * drag, so the row does not jump under the cursor at lift-off. */
      let grabOffset = 0;
      let height = 0;
      /** Offsets of every slot from the container's top, cached at lift-off. */
      let slotTops: number[] = [];
      /** The dragged row's live index — the placeholder's position. */
      let liveIndex = index;

      const onMove = (ev: MouseEvent) => {
        const container = containerRef.current;
        if (!container) return;

        if (!started) {
          if (
            Math.max(Math.abs(ev.clientX - startX), Math.abs(ev.clientY - startY)) < DRAG_DISTANCE
          ) {
            return;
          }
          const rowRect = row.getBoundingClientRect();
          const containerTop = container.getBoundingClientRect().top;
          grabOffset = startY - rowRect.top;
          height = rowRect.height;
          slotTops = Array.from(
            container.querySelectorAll<HTMLElement>(':scope > [data-index]')
          ).map((el) => el.getBoundingClientRect().top - containerTop);
          started = true;
          // Legacy's `disableSelection` — a bare mouse drag would otherwise
          // paint a text selection down the rail. Done by suppressing selection
          // for the drag's duration rather than by `preventDefault`ing the
          // `mousedown`, which would also swallow the focus change and with it
          // the blur-commit the right-hand pane depends on.
          document.body.style.userSelect = 'none';
          window.getSelection()?.removeAllRanges();
        }

        // `scroll: true` — page the rail when the pointer nears either edge.
        const scroller = scrollerRef.current;
        if (scroller) {
          const box = scroller.getBoundingClientRect();
          if (ev.clientY - box.top < SCROLL_SENSITIVITY) {
            scroller.scrollTop -= SCROLL_SPEED;
          } else if (box.bottom - ev.clientY < SCROLL_SENSITIVITY) {
            scroller.scrollTop += SCROLL_SPEED;
          }
        }

        // ★ (1) + (2): only Y is ever computed, and it is clamped into the
        // container — the helper cannot leave the list in either direction.
        const containerTop = container.getBoundingClientRect().top;
        const maxTop = Math.max(0, container.offsetHeight - height);
        const top = Math.min(Math.max(ev.clientY - grabOffset - containerTop, 0), maxTop);

        // The slot the helper is nearest to, off the cached geometry.
        const count = latest.current.rowCount;
        let to = liveIndex;
        if (slotTops.length > 0) {
          let best = Infinity;
          slotTops.forEach((slotTop, i) => {
            const d = Math.abs(slotTop - top);
            if (d < best) {
              best = d;
              to = i;
            }
          });
        }
        to = Math.min(Math.max(to, 0), Math.max(count - 1, 0));

        setDrag({ index: to, top, height });
        if (to !== liveIndex) {
          latest.current.onRowsMove(liveIndex, to);
          liveIndex = to;
        }
        // Suppress the text selection a bare mouse-drag would otherwise paint
        // across the rail (legacy's `disableSelection`).
        ev.preventDefault();
      };

      const release = () => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
        if (started) document.body.style.userSelect = '';
        releaseRef.current = null;
      };

      const onUp = () => {
        release();
        if (!started) return;
        setDrag(null);
        justDraggedRef.current = true;
        window.setTimeout(() => {
          justDraggedRef.current = false;
        }, 0);
        // ★ `stop` has no "did anything actually move" test, and neither does
        // this: legacy PATCHes the ordering on every drag that started.
        latest.current.onCommit();
      };

      releaseRef.current?.();
      releaseRef.current = release;
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    },
    [scrollerRef]
  );

  const helperStyle = useCallback(
    (index: number): React.CSSProperties | undefined =>
      drag && drag.index === index
        ? {
            // `.select-row` is `position: relative` in `user_settings/styles.css`;
            // this inline rule is what lifts the row out of flow so the
            // placeholder can hold its slot open.
            position: 'absolute',
            left: 0,
            right: 0,
            top: drag.top,
            zIndex: 100,
          }
        : undefined,
    [drag]
  );

  return { drag, containerRef, onRowMouseDown, justDraggedRef, helperStyle };
};
